import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { languages } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe, Check } from "lucide-react";
import { LOCALIZED_PATHS } from "@/lib/localizedPaths";

const LOCALE_SET = new Set(languages.map((l) => l.code));

const LanguageSelector = ({
  variant = "default",
  showFlag = true,
  testId = "language-selector",
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang =
    languages.find((l) => l.code === i18n.language) || languages[1]; // Default to French

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    // On pages with real per-locale URLs, drive the URL too (/en/pricing…,
    // French at root) so the language is reflected in the address for SEO. On
    // other pages the language just switches client-side (URL unchanged).
    const parts = location.pathname.split("/").filter(Boolean);
    const base =
      parts.length && LOCALE_SET.has(parts[0])
        ? "/" + parts.slice(1).join("/")
        : "/" + parts.join("/");
    const cleanBase = base === "" ? "/" : base;
    if (LOCALIZED_PATHS.has(cleanBase)) {
      navigate(
        code === "fr"
          ? cleanBase
          : `/${code}${cleanBase === "/" ? "" : cleanBase}`
      );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === "ghost" ? "ghost" : "outline"}
          size="sm"
          className={
            variant === "ghost"
              ? "text-slate-400 hover:text-white"
              : "border-white/20 text-white hover:bg-white/5"
          }
          data-testid={testId}
        >
          <Globe className="w-4 h-4 mr-2 shrink-0" />
          {showFlag && <span className="mr-1">{currentLang.flag}</span>}
          {/* Always show the language name so the switcher has the same
              "🌐 <flag> <name>" form on every page and every viewport
              (was `hidden sm:inline`, which collapsed to just the flag on
              mobile — inconsistent with the desktop form). */}
          <span className="whitespace-nowrap">{currentLang.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-card border-white/10">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="cursor-pointer flex items-center justify-between"
            data-testid={`lang-option-${lang.code}`}
          >
            <span className="flex items-center gap-2">
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
            </span>
            {i18n.language === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
